import { useCallback, useMemo, useRef } from 'react';
import { Button } from 'src/components/Button';
import { Option } from 'src/components/SelectBox';
import useAuthedRequest from 'src/hooks/useAuthedRequest';
import { Agent } from 'src/interfaces/agent.interface';
import { CommunicationChannel } from 'src/services/campaigns/campaigns.interface';
import {
  AGENT_OPTION_SAME_AS_INITIAL_CALL,
  FollowUpCommunicationForm,
  generateListElementId,
  useWizard,
} from './components/wizard/WizardContext';

import { ArrowConnector } from 'src/components/ArrowConnector';
import { CallStepCard } from './components/ui/call/CallStepCard';
import { DaysAfterSelector } from './components/ui/DaysAfterSelector';
import { EmailStepCard } from './components/ui/email/EmailStepCard';
import { SmsStepCard } from './components/ui/sms/SmsStepCard';
import { cn } from 'src/utils/className';

export function StepBuilder({
  hideTimeWindow = true,
  disabled = false,
}: {
  hideTimeWindow?: boolean;
  disabled?: boolean;
}) {
  const { campaignAutomation, setCampaignAutomation } = useWizard();
  const { get } = useAuthedRequest();

  const agentsPromiseCache = useRef<
    Record<string, Promise<Option[]> | undefined>
  >({});

  const communicationSettings = useMemo(
    () => ({
      [CommunicationChannel.SMS]: {
        label: 'SMS',
      },
      [CommunicationChannel.EMAIL]: {
        label: 'Email',
      },
      [CommunicationChannel.CALL]: {
        label: 'Phone Call',
      },
    }),
    [],
  );

  const fetchAgentOptions = useCallback(
    (name: string) => {
      const normalized = (name || '').trim().toLowerCase();

      const cachedPromise = agentsPromiseCache.current[normalized];
      if (cachedPromise) {
        return cachedPromise;
      }

      const query = new URLSearchParams(
        (normalized
          ? {
              filters: JSON.stringify({ name: normalized }),
            }
          : {}) as Record<string, string>,
      );

      const promise = get(`/agents?${query.toString()}`)
        .then((data) =>
          data.items.map((agent: Agent) => ({
            value: agent.id,
            label: agent.name,
          })),
        )
        .catch((error) => {
          delete agentsPromiseCache.current[normalized];
          throw error;
        });

      agentsPromiseCache.current[normalized] = promise;
      return promise;
    },
    [get],
  );

  const createDefaultFollowUpStep = useCallback(
    (): FollowUpCommunicationForm => ({
      id: generateListElementId(),
      daysAfter: 1,
      contactVia: CommunicationChannel.CALL,
      agentOption: AGENT_OPTION_SAME_AS_INITIAL_CALL,
    }),
    [],
  );

  const updateFollowUp = useCallback(
    (index: number, changes: Partial<FollowUpCommunicationForm>) => {
      const current = campaignAutomation.followUpCommunications || [];
      if (!Number.isInteger(index) || index < 0 || index >= current.length) {
        return;
      }
      const next = [...current];
      next[index] = {
        ...next[index],
        ...changes,
      } as FollowUpCommunicationForm;
      setCampaignAutomation({
        ...campaignAutomation,
        followUpCommunications: next,
      });
    },
    [campaignAutomation, setCampaignAutomation],
  );

  const insertFollowUp = useCallback(
    (at: number, step?: FollowUpCommunicationForm) => {
      const next = [...campaignAutomation.followUpCommunications];
      next.splice(at, 0, step || createDefaultFollowUpStep());
      setCampaignAutomation({
        ...campaignAutomation,
        followUpCommunications: next,
      });
    },
    [campaignAutomation, setCampaignAutomation, createDefaultFollowUpStep],
  );

  const removeFollowUp = useCallback(
    (index: number) => {
      const next = [...campaignAutomation.followUpCommunications];
      next.splice(index, 1);
      setCampaignAutomation({
        ...campaignAutomation,
        followUpCommunications: next,
      });
    },
    [campaignAutomation, setCampaignAutomation],
  );

  const renderFollowUpCard = useCallback(
    (step: FollowUpCommunicationForm, index: number) => {
      const commonProps = {
        stepNumber: index + 2,
        onTitleChange: (value: string) =>
          updateFollowUp(index, {
            contactVia: value as CommunicationChannel,
          } as FollowUpCommunicationForm),
        addStepAbove: () => insertFollowUp(index),
        addStepBelow: () => insertFollowUp(index + 1),
        deleteStep: () => removeFollowUp(index),
      } as const;

      if (step.contactVia === CommunicationChannel.CALL) {
        return (
          <CallStepCard
            required
            selectedAgentOption={
              step.agentOption || AGENT_OPTION_SAME_AS_INITIAL_CALL
            }
            onAgentChange={(agentOption) =>
              updateFollowUp(index, { agentOption })
            }
            fetchAgentOptions={fetchAgentOptions}
            hideTimeWindow={hideTimeWindow}
            {...commonProps}
            disabled={disabled}
          />
        );
      }

      if (step.contactVia === CommunicationChannel.EMAIL) {
        return (
          <EmailStepCard
            subject={step.emailSubject || ''}
            body={step.emailBody || ''}
            onSubjectChange={(value) =>
              updateFollowUp(index, { emailSubject: value })
            }
            onBodyChange={(value) =>
              updateFollowUp(index, { emailBody: value })
            }
            hideTimeWindow={hideTimeWindow}
            {...commonProps}
            disabled={disabled}
          />
        );
      }

      if (step.contactVia === CommunicationChannel.SMS) {
        return (
          <SmsStepCard
            body={step.smsBody || ''}
            onBodyChange={(value) => updateFollowUp(index, { smsBody: value })}
            hideTimeWindow={hideTimeWindow}
            {...commonProps}
            disabled={disabled}
          />
        );
      }

      return null;
    },
    [
      hideTimeWindow,
      fetchAgentOptions,
      insertFollowUp,
      updateFollowUp,
      removeFollowUp,
      disabled,
    ],
  );

  return (
    <div className="flex flex-col gap-y-4">
      {/* Trigger */}
      <div className="flex justify-center">
        <div className="inline-flex items-center px-4 py-3 border border-neutral-200 rounded-2xl">
          <p className="text-base text-neutral-600">
            When a <span className="font-medium">contact is added</span>
          </p>
        </div>
      </div>
      <ArrowConnector />
      {/* Step 1 - Initial Call */}
      <CallStepCard
        hideTimeWindow={hideTimeWindow}
        stepNumber={1}
        required
        selectedAgentOption={campaignAutomation.agentOption}
        onAgentChange={(option) =>
          setCampaignAutomation({
            ...campaignAutomation,
            agentOption: option,
          })
        }
        fetchAgentOptions={fetchAgentOptions}
        addStepBelow={() => insertFollowUp(0)}
        disabled={disabled}
      />
      {/* Follow-up steps */}
      {campaignAutomation.followUpCommunications.map((step, index) => {
        const prevLabel =
          index === 0
            ? 'phone call'
            : communicationSettings[
                campaignAutomation.followUpCommunications[index - 1].contactVia
              ]?.label.toLowerCase();

        return (
          <div key={step.id} className="space-y-4">
            {/* Delay selector between steps */}

            <ArrowConnector noArrow />

            <DaysAfterSelector
              prevLabel={prevLabel}
              daysAfter={step.daysAfter}
              onChange={(newDaysAfter) => {
                const next = [...campaignAutomation.followUpCommunications];
                next[index] = {
                  ...step,
                  daysAfter: newDaysAfter,
                };
                setCampaignAutomation({
                  ...campaignAutomation,
                  followUpCommunications: next,
                });
              }}
              disabled={disabled}
            />

            <ArrowConnector />

            {renderFollowUpCard(step, index)}
          </div>
        );
      })}
      <ArrowConnector />

      {/* Add next outreach */}
      <div className="flex justify-center">
        <Button
          type="button"
          variant="outlined"
          onClick={() =>
            insertFollowUp(campaignAutomation.followUpCommunications.length)
          }
          className={cn('border-dashed', disabled && 'cursor-not-allowed')}
          disabled={disabled}
        >
          + Add next outreach
        </Button>
      </div>
    </div>
  );
}
export default StepBuilder;
